import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { getDownloadUrl } from '../apis/fileApi';

/**
 * Hook to fetch a presigned download URL for rendering a document preview.
 * Returns the URL for use in PDF.js or <img> src.
 */
export const useDocumentPreview = (storageKey: string | undefined) => {
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading, error } = useQuery({
    queryKey: ['document-preview-url', storageKey],
    queryFn: async () => {
      if (!storageKey) throw new Error('storageKey is required');
      const token = await getAccessTokenSilently();
      const response = await getDownloadUrl(storageKey, token);
      return response.url;
    },
    enabled: !!storageKey,
    staleTime: 30 * 60 * 1000, // 30 minutes (presigned URL valid for 1 hour)
    // Keep URL cached as long as it's fresh so navigating away and back
    // doesn't trigger a re-fetch before the presigned URL actually expires.
    gcTime: 30 * 60 * 1000,
  });

  return {
    documentUrl: data ?? null,
    isLoading,
    error: error as Error | null,
  };
};
